import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';

interface Position {
    id: string;
    market: string;
    symbol: string;
    status: string;
    openedAt: string;
    closedAt: string | null;
    avgPrice: number;
    totalQty: number;
    realizedPnl: number | null;
    realizedPnlPct: number | null;
    effectiveStopLossPct: number;
    stopLossPctOverride: number | null;
    takeProfitPct: number | null;
    rationaleTags: string[] | null;
    rationaleText: string | null;
    counterRationaleText: string | null;
    fills: Fill[];
    attachments: Attachment[];
    review: Review | null;
}

interface Fill {
    id: string;
    type: string;
    at: string;
    price: number;
    qty: number;
    memo: string | null;
}

interface Attachment {
    id: string;
    type: string;
    url: string;
    caption: string | null;
    createdAt: string;
}

interface Review {
    id: string;
    answersJson: any;
    reviewText: string | null;
    computedPnlPct: number | null;
    stopLossRespected: boolean | null;
}

export default function PositionDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [position, setPosition] = useState<Position | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'fills' | 'attachments' | 'review'>('fills');
    const [reviewText, setReviewText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Price data
    const [currentPrice, setCurrentPrice] = useState<number | null>(null);
    const [stockName, setStockName] = useState<string | null>(null);
    const [priceLoading, setPriceLoading] = useState(false);

    // Edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [editStopLoss, setEditStopLoss] = useState('');
    const [editTakeProfit, setEditTakeProfit] = useState('');
    const [editRationale, setEditRationale] = useState('');
    const [editCounterRationale, setEditCounterRationale] = useState('');

    // Image upload state
    interface UploadingFile {
        file: File;
        preview: string;
        r2Key?: string;
        url?: string;
        uploading: boolean;
        error?: string;
    }
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadPosition();
    }, [id]);

    async function loadPosition() {
        if (!id) return;
        const response = await api.getPosition(id);
        if (response.success) {
            setPosition(response.data);
            if (response.data.review?.reviewText) {
                setReviewText(response.data.review.reviewText);
            }
            // Load current price for OPEN positions
            if (response.data.status === 'OPEN') {
                loadPrice(response.data.symbol, response.data.market);
            }
        }
        setIsLoading(false);
    }

    async function loadPrice(symbol: string, market: string) {
        setPriceLoading(true);
        // Map market names to API format
        const apiMarket = market.includes('KO') || market === 'KOSPI' || market === 'KOSDAQ' || market === 'KRX' ? 'KRX' : 'US';
        const response = await api.getStockPrice(symbol, apiMarket);
        if (response.success) {
            setCurrentPrice(response.data.currentPrice);
            if (response.data.stockName) {
                setStockName(response.data.stockName);
            }
        }
        setPriceLoading(false);
    }

    async function handleRefreshPrice() {
        if (!position) return;
        await loadPrice(position.symbol, position.market);
    }

    // Close position modal state
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [closePrice, setClosePrice] = useState('');

    function openCloseModal() {
        // Pre-fill with current price if available
        setClosePrice(currentPrice?.toString() || '');
        setShowCloseModal(true);
    }

    async function handleCloseWithPrice() {
        if (!id || !closePrice) {
            alert('청산 단가를 입력해주세요');
            return;
        }

        const closePriceNum = parseFloat(closePrice);
        if (isNaN(closePriceNum) || closePriceNum <= 0) {
            alert('유효한 청산 단가를 입력해주세요');
            return;
        }

        setIsSubmitting(true);
        const response = await api.closePosition(id, closePriceNum);
        if (response.success) {
            setShowCloseModal(false);
            loadPosition();
        } else {
            alert('error' in response ? response.error : '청산에 실패했습니다');
        }
        setIsSubmitting(false);
    }

    async function handleDelete() {
        if (!id || !confirm('정말로 이 포트폴리오를 삭제하시겠습니까?\n\n삭제된 데이터는 복구할 수 없습니다.')) return;

        setIsSubmitting(true);
        const response = await api.deletePosition(id);
        if (response.success) {
            navigate('/positions');
        } else {
            alert('error' in response ? response.error : '삭제에 실패했습니다');
        }
        setIsSubmitting(false);
    }

    function startEditing() {
        if (!position) return;
        setEditStopLoss(position.stopLossPctOverride?.toString() || '');
        setEditTakeProfit(position.takeProfitPct?.toString() || '');
        setEditRationale(position.rationaleText || '');
        setEditCounterRationale(position.counterRationaleText || '');
        setIsEditing(true);
    }

    function cancelEditing() {
        setIsEditing(false);
    }

    async function handleSaveEdit() {
        if (!id) return;

        setIsSubmitting(true);
        const response = await api.updatePosition(id, {
            stopLossPctOverride: editStopLoss ? parseFloat(editStopLoss) : null,
            takeProfitPct: editTakeProfit ? parseFloat(editTakeProfit) : null,
            rationaleText: editRationale || null,
            counterRationaleText: editCounterRationale || null,
        });

        if (response.success) {
            setIsEditing(false);
            loadPosition();
        } else {
            alert('error' in response ? response.error : '수정에 실패했습니다');
        }
        setIsSubmitting(false);
    }

    async function handleSaveReview() {
        if (!id) return;

        setIsSubmitting(true);
        const response = await api.createReview(id, { reviewText });
        if (response.success) {
            loadPosition();
        }
        setIsSubmitting(false);
    }

    // Image utilities
    function compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob failed'));
                            return;
                        }
                        const compressedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    }, file.type, quality);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    }

    async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
        const selectedFiles = e.target.files;
        if (!selectedFiles) return;

        const filePromises = Array.from(selectedFiles).map(async (file) => {
            try {
                const compressedFile = await compressImage(file);
                return {
                    file: compressedFile,
                    preview: URL.createObjectURL(compressedFile),
                    uploading: false,
                } as UploadingFile;
            } catch {
                return {
                    file,
                    preview: URL.createObjectURL(file),
                    uploading: false,
                } as UploadingFile;
            }
        });

        const newFiles = await Promise.all(filePromises);
        setUploadingFiles(prev => [...prev, ...newFiles]);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    function removeUploadingFile(index: number) {
        setUploadingFiles(prev => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
    }

    async function uploadSingleFile(index: number) {
        if (!id) return;
        const file = uploadingFiles[index];
        if (file.uploading || file.r2Key) return;

        setUploadingFiles(prev => {
            const newFiles = [...prev];
            newFiles[index] = { ...newFiles[index], uploading: true, error: undefined };
            return newFiles;
        });

        try {
            // 1. Upload File
            const uploadResponse = await api.uploadFile(file.file);

            if (!uploadResponse.success) {
                throw new Error(uploadResponse.error || '업로드 실패');
            }

            const { fileKey, fileUrl } = uploadResponse.data;

            // 2. Register Attachment in DB
            const registerResponse = await api.addAttachment(id, {
                type: 'execution', // Default type
                r2Key: fileKey,
                url: fileUrl,
            });

            if (registerResponse.success) {
                setUploadingFiles(prev => prev.filter((_, i) => i !== index));
                loadPosition(); // Reload to show new attachment
            } else {
                throw new Error(registerResponse.error || 'DB 등록 실패');
            }

        } catch (e: any) {
            setUploadingFiles(prev => {
                const newFiles = [...prev];
                newFiles[index] = {
                    ...newFiles[index],
                    uploading: false,
                    error: e.message || '업로드 실패'
                };
                return newFiles;
            });
        }
    }

    async function uploadAllFiles() {
        for (let i = 0; i < uploadingFiles.length; i++) {
            if (!uploadingFiles[i].r2Key && !uploadingFiles[i].error) {
                await uploadSingleFile(i);
            }
        }
    }

    async function deleteAttachment(attachmentId: string) {
        if (!confirm('이미지를 삭제하시겠습니까?')) return;
        const response = await api.deleteAttachment(attachmentId);
        if (response.success) {
            loadPosition();
        }
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString('ko-KR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatPnl = (pnl: number | null | undefined) => {
        if (pnl == null) return '-';
        const formatted = pnl.toFixed(2);
        return pnl >= 0 ? `+${formatted}%` : `${formatted}%`;
    };

    const getPnlClass = (pnl: number | null | undefined) => {
        if (pnl == null) return '';
        return pnl >= 0 ? 'text-success' : 'text-danger';
    };

    if (isLoading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (!position) {
        return (
            <div className="card">
                <div className="empty-state">
                    <p>포트폴리오를 찾을 수 없습니다</p>
                    <button onClick={() => navigate('/positions')} className="btn btn-primary mt-md">
                        목록으로 돌아가기
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <div className="flex items-center gap-sm mb-xs">
                        <h1 className="text-xl font-bold">{position.symbol}</h1>
                        {stockName && <span className="text-lg text-muted font-normal">{stockName}</span>}
                    </div>
                    <p className="text-muted">{position.market}</p>
                </div>
                <div className="flex gap-sm">
                    {position.status === 'OPEN' && (
                        <button
                            onClick={openCloseModal}
                            className="btn btn-danger"
                            disabled={isSubmitting}
                        >
                            청산하기
                        </button>
                    )}
                    <button
                        onClick={handleDelete}
                        className="btn btn-ghost text-danger"
                        disabled={isSubmitting}
                        style={{ border: '1px solid var(--color-danger)' }}
                    >
                        삭제
                    </button>
                </div>
            </div>

            {/* Overview - Calculate from fills */}
            {(() => {
                const buyFills = position.fills?.filter(f => f.type === 'BUY') || [];
                const totalQty = buyFills.reduce((sum, f) => sum + f.qty, 0);
                const totalCost = buyFills.reduce((sum, f) => sum + (f.price * f.qty), 0);
                const avgPrice = totalQty > 0 ? totalCost / totalQty : 0;
                const unrealizedPnlPct = currentPrice && avgPrice > 0
                    ? ((currentPrice - avgPrice) / avgPrice) * 100
                    : null;

                return (
                    <div className="stats-grid mb-lg" style={{ fontSize: '0.9em' }}>
                        <div className="stat-card">
                            <div className="stat-value" style={{ fontSize: '1.1rem' }}>
                                {avgPrice > 0 ? avgPrice.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}원
                            </div>
                            <div className="stat-label">평균단가</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value" style={{ fontSize: '1.1rem' }}>
                                {totalQty > 0 ? totalQty.toLocaleString() : '-'}주
                            </div>
                            <div className="stat-label">보유수량</div>
                        </div>
                        {position.status === 'OPEN' && (
                            <div className="stat-card">
                                <div className="stat-value" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '1.1rem' }}>
                                    {priceLoading ? (
                                        <span className="text-muted">로딩...</span>
                                    ) : currentPrice ? (
                                        <>
                                            {currentPrice.toLocaleString()}원
                                            <button
                                                onClick={handleRefreshPrice}
                                                className="btn btn-ghost btn-sm"
                                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                                disabled={priceLoading}
                                            >
                                                🔄
                                            </button>
                                        </>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </div>
                                <div className="stat-label">현재가</div>
                            </div>
                        )}
                        {position.status === 'OPEN' && unrealizedPnlPct !== null && (
                            <div className="stat-card">
                                <div className={`stat-value ${unrealizedPnlPct >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.1rem' }}>
                                    {unrealizedPnlPct >= 0 ? '+' : ''}{unrealizedPnlPct.toFixed(2)}%
                                </div>
                                <div className="stat-label">수익률</div>
                            </div>
                        )}
                        {position.status === 'CLOSED' && (
                            <div className="stat-card">
                                <div className={`stat-value ${getPnlClass(position.realizedPnlPct)}`} style={{ fontSize: '1.1rem' }}>
                                    {formatPnl(position.realizedPnlPct)}
                                </div>
                                <div className="stat-label">실현손익</div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Status */}
            <div className="card mb-lg">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-md">
                        <span className={`badge ${position.status === 'OPEN' ? 'badge-success' : 'badge-neutral'}`}>
                            {position.status === 'OPEN' ? '보유중' : '청산'}
                        </span>
                        <span className="text-sm text-muted">
                            진입: {formatDate(position.openedAt)}
                        </span>
                        {position.closedAt && (
                            <span className="text-sm text-muted">
                                청산: {formatDate(position.closedAt)}
                            </span>
                        )}
                    </div>
                    {position.review && position.review.stopLossRespected !== null && (
                        <span className={`badge ${position.review.stopLossRespected ? 'badge-success' : 'badge-danger'}`}>
                            손절 {position.review.stopLossRespected ? '준수' : '미준수'}
                        </span>
                    )}
                </div>
            </div>

            {/* Rationale - Editable */}
            <div className="card mb-lg">
                <div className="card-header flex justify-between items-center">
                    <h3 className="card-title mb-0">진입 근거</h3>
                    {!isEditing ? (
                        <button onClick={startEditing} className="btn btn-secondary btn-sm">
                            ✏️ 수정
                        </button>
                    ) : (
                        <div className="flex gap-sm">
                            <button onClick={cancelEditing} className="btn btn-ghost btn-sm">
                                취소
                            </button>
                            <button onClick={handleSaveEdit} className="btn btn-primary btn-sm" disabled={isSubmitting}>
                                {isSubmitting ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    )}
                </div>

                {isEditing ? (
                    <div className="flex flex-col gap-md" style={{ paddingTop: 'var(--space-md)' }}>
                        <div className="form-group">
                            <label className="form-label">손절 % (커스텀)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={editStopLoss}
                                onChange={(e) => setEditStopLoss(e.target.value)}
                                placeholder="예: 5"
                                step="0.1"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">익절 %</label>
                            <input
                                type="number"
                                className="form-input"
                                value={editTakeProfit}
                                onChange={(e) => setEditTakeProfit(e.target.value)}
                                placeholder="예: 10"
                                step="0.1"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">매수 이유</label>
                            <textarea
                                className="form-input form-textarea"
                                value={editRationale}
                                onChange={(e) => setEditRationale(e.target.value)}
                                placeholder="왜 이 종목을 매수했나요?"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">반대 시나리오</label>
                            <textarea
                                className="form-input form-textarea"
                                value={editCounterRationale}
                                onChange={(e) => setEditCounterRationale(e.target.value)}
                                placeholder="어떤 상황에서 손절할 것인가?"
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        {position.rationaleTags && (() => {
                            const tags = typeof position.rationaleTags === 'string'
                                ? JSON.parse(position.rationaleTags)
                                : position.rationaleTags;
                            return Array.isArray(tags) && tags.length > 0 ? (
                                <div className="flex gap-sm mb-md" style={{ marginTop: 'var(--space-md)' }}>
                                    {tags.map((tag: string, idx: number) => (
                                        <span key={idx} className="badge badge-info">{tag}</span>
                                    ))}
                                </div>
                            ) : null;
                        })()}
                        {position.rationaleText ? (
                            <div className="mb-md" style={{ marginTop: 'var(--space-sm)' }}>
                                <h4 className="text-sm font-semibold mb-sm">매수 이유</h4>
                                <p>{position.rationaleText}</p>
                            </div>
                        ) : (
                            <p className="text-muted" style={{ marginTop: 'var(--space-md)' }}>매수 이유가 입력되지 않았습니다.</p>
                        )}
                        {position.counterRationaleText && (
                            <div>
                                <h4 className="text-sm font-semibold mb-sm">반대 시나리오</h4>
                                <p>{position.counterRationaleText}</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`btn ${activeTab === 'fills' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('fills')}
                >
                    체결 ({position.fills.length})
                </button>
                <button
                    className={`btn ${activeTab === 'attachments' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('attachments')}
                >
                    첨부 ({position.attachments.length})
                </button>
                <button
                    className={`btn ${activeTab === 'review' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('review')}
                >
                    복기 {position.review ? '✓' : ''}
                </button>
            </div>

            {/* Fills Tab */}
            {activeTab === 'fills' && (
                <div className="card">
                    <div className="timeline">
                        {position.fills.map((fill) => (
                            <div key={fill.id} className="timeline-item">
                                <div className="timeline-item-header">
                                    <span className={`badge ${fill.type === 'BUY' ? 'badge-success' : 'badge-danger'}`}>
                                        {fill.type === 'BUY' ? '매수' : '매도'}
                                    </span>
                                    <span className="timeline-item-date">{formatDate(fill.at)}</span>
                                </div>
                                <div className="flex gap-lg text-sm">
                                    <span>{fill.price.toLocaleString()}원</span>
                                    <span>{fill.qty.toLocaleString()}주</span>
                                    <span className="text-muted">
                                        총 {(fill.price * fill.qty).toLocaleString()}원
                                    </span>
                                </div>
                                {fill.memo && <p className="text-sm text-muted mt-sm">{fill.memo}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Attachments Tab */}
            {activeTab === 'attachments' && (
                <div className="card">
                    <div className="mb-md">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <div
                            className="upload-zone mb-md"
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: '2px dashed var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                padding: 'var(--space-lg)',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: 'var(--color-bg-secondary)',
                            }}
                        >
                            <p style={{ margin: 0, fontWeight: 600 }}>클릭하여 이미지 선택</p>
                            <p className="text-sm text-muted" style={{ margin: 0 }}>매매 증빙, 차트 등</p>
                        </div>

                        {/* Uploading files */}
                        {uploadingFiles.length > 0 && (
                            <div className="mb-lg">
                                <p className="text-sm font-semibold mb-sm">업로드 대기 ({uploadingFiles.length})</p>
                                <div className="image-gallery">
                                    {uploadingFiles.map((file, idx) => (
                                        <div key={idx} className="gallery-item" style={{ position: 'relative' }}>
                                            <img src={file.preview} alt={file.file.name} />
                                            <div className="gallery-item-overlay">
                                                {file.uploading ? (
                                                    <div className="loading-spinner" style={{ width: 24, height: 24 }}></div>
                                                ) : file.error ? (
                                                    <span className="badge badge-danger">{file.error}</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        onClick={(e) => { e.stopPropagation(); uploadSingleFile(idx); }}
                                                    >
                                                        업로드
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={(e) => { e.stopPropagation(); removeUploadingFile(idx); }}
                                                    style={{ marginLeft: '4px' }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary mt-sm"
                                    onClick={uploadAllFiles}
                                >
                                    전체 업로드
                                </button>
                            </div>
                        )}
                    </div>

                    {position.attachments.length > 0 ? (
                        <div className="image-gallery">
                            {position.attachments.map((att) => (
                                <div key={att.id} className="gallery-item" style={{ position: 'relative' }}>
                                    <img src={att.url} alt={att.caption || '첨부 이미지'} />
                                    <div className="gallery-item-overlay">
                                        <span className="badge badge-neutral">{att.type}</span>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            style={{ marginLeft: '4px' }}
                                            onClick={(e) => { e.stopPropagation(); deleteAttachment(att.id); }}
                                        >삭제</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>첨부된 이미지가 없습니다</p>
                        </div>
                    )}
                </div>
            )}

            {/* Review Tab */}
            {activeTab === 'review' && (
                <div className="card">
                    <h3 className="card-title mb-md">복기</h3>

                    {position.review?.computedPnlPct !== null && (
                        <div className="mb-lg">
                            <span className="text-sm text-muted">최종 수익률: </span>
                            <span className={`font-semibold ${getPnlClass(position.review?.computedPnlPct ?? null)}`}>
                                {formatPnl(position.review?.computedPnlPct ?? null)}
                            </span>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">복기 메모 (1~3문장)</label>
                        <textarea
                            className="form-input form-textarea"
                            value={reviewText}
                            onChange={(e) => setReviewText(e.target.value)}
                            placeholder="이번 거래에서 배운 점, 개선할 점을 기록하세요..."
                            maxLength={500}
                        />
                    </div>

                    <button
                        onClick={handleSaveReview}
                        className="btn btn-primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? '저장 중...' : '복기 저장'}
                    </button>
                </div>
            )}

            {/* Close Position Modal */}
            {showCloseModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div className="modal card" style={{
                        maxWidth: '400px',
                        width: '90%',
                    }}>
                        <h3 className="card-title mb-md">포트폴리오 청산</h3>
                        <div className="form-group mb-md">
                            <label className="form-label">청산 단가 (원)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={closePrice}
                                onChange={(e) => setClosePrice(e.target.value)}
                                placeholder="청산 단가를 입력하세요"
                                autoFocus
                            />
                            {closePrice && position.avgPrice && (
                                <p className="text-sm mt-sm" style={{
                                    color: parseFloat(closePrice) >= position.avgPrice ? 'var(--color-success)' : 'var(--color-danger)'
                                }}>
                                    예상 수익률: {(((parseFloat(closePrice) - position.avgPrice) / position.avgPrice) * 100).toFixed(2)}%
                                </p>
                            )}
                        </div>
                        <div className="flex gap-sm justify-end">
                            <button
                                onClick={() => setShowCloseModal(false)}
                                className="btn btn-ghost"
                                disabled={isSubmitting}
                            >
                                취소
                            </button>
                            <button
                                onClick={handleCloseWithPrice}
                                className="btn btn-danger"
                                disabled={isSubmitting || !closePrice}
                            >
                                {isSubmitting ? '처리 중...' : '청산 확정'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Add simple helper for uploadToR2 if needed, or use inline fetch as done above.
// The inline fetch is sufficient as it mirrors the core logic.
